import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, RefreshCw } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    updateEmailScanConfig,
    scanEmailAccountNow,
    listPlugins,
    listTemplates,
    listWorkgroups,
    listWorkgroupMembers,
} from "../../lib/api";

// iter-102 — Scan Inbox + Auto-Quote configuration dialog. Lets the
// user toggle inbox scanning, pick plugins/templates for auto-quote,
// optionally share into a workgroup, and choose who to notify.
export default function ScanConfigDialog({ open, onOpenChange, account, onUpdated }) {
    const [scanIncoming, setScanIncoming] = useState(false);
    const [autoQuote, setAutoQuote] = useState(false);
    const [pluginIds, setPluginIds] = useState([]);
    const [templateIds, setTemplateIds] = useState([]);
    const [workgroupId, setWorkgroupId] = useState("");
    const [notifyKind, setNotifyKind] = useState("none"); // "none" | "all" | "select"
    const [notifyMemberIds, setNotifyMemberIds] = useState([]);

    const [plugins, setPlugins] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [workgroups, setWorkgroups] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scanning, setScanning] = useState(false);

    // Sync local state from the account whenever the dialog opens.
    useEffect(() => {
        if (!open || !account) return;
        setScanIncoming(!!account.scan_incoming);
        setAutoQuote(!!account.auto_quote);
        setPluginIds(account.scan_plugins || []);
        setTemplateIds(account.scan_templates || []);
        setWorkgroupId(account.scan_workgroup_id || "");
        const nm = account.scan_notify_members;
        if (nm === "all") setNotifyKind("all");
        else if (Array.isArray(nm) && nm.length) {
            setNotifyKind("select");
            setNotifyMemberIds(nm);
        } else {
            setNotifyKind("none");
            setNotifyMemberIds([]);
        }
    }, [open, account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load static lookup data once when dialog opens.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const [pl, tpl, wgs] = await Promise.all([
                    listPlugins().catch(() => []),
                    listTemplates().catch(() => []),
                    listWorkgroups().catch(() => []),
                ]);
                if (cancelled) return;
                setPlugins(Array.isArray(pl) ? pl : (pl?.plugins || []));
                setTemplates(Array.isArray(tpl) ? tpl : (tpl?.templates || []));
                setWorkgroups(Array.isArray(wgs) ? wgs : (wgs?.workgroups || []));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Reload members whenever the chosen workgroup changes.
    useEffect(() => {
        if (!workgroupId) { setMembers([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const m = await listWorkgroupMembers(workgroupId);
                if (!cancelled) setMembers(Array.isArray(m) ? m : (m?.members || []));
            } catch {
                if (!cancelled) setMembers([]);
            }
        })();
        return () => { cancelled = true; };
    }, [workgroupId]);

    const togglePlugin = (id) =>
        setPluginIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    const toggleTemplate = (id) =>
        setTemplateIds((t) => t.includes(id) ? t.filter((x) => x !== id) : [...t, id]);
    const toggleMember = (id) =>
        setNotifyMemberIds((m) => m.includes(id) ? m.filter((x) => x !== id) : [...m, id]);

    const handleSave = async () => {
        if (!account) return;
        setSaving(true);
        try {
            let notifyValue = null;
            if (notifyKind === "all") notifyValue = "all";
            else if (notifyKind === "select") notifyValue = notifyMemberIds;
            const next = await updateEmailScanConfig(account.id, {
                scan_incoming: scanIncoming,
                auto_quote: autoQuote,
                scan_plugins: pluginIds,
                scan_templates: templateIds,
                scan_workgroup_id: workgroupId || "",
                scan_notify_members: notifyValue,
            });
            onUpdated?.(next);
            toast.success(scanIncoming ? "Inbox scanning enabled" : "Settings saved");
            onOpenChange?.(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save settings");
        } finally {
            setSaving(false);
        }
    };

    const handleScanNow = async () => {
        if (!account) return;
        setScanning(true);
        try {
            const r = await scanEmailAccountNow(account.id);
            if (r?.ok) {
                toast.success(
                    `Scan complete · ${r.processed || 0} processed · ${r.skipped || 0} skipped`,
                );
            } else {
                toast.message(`Scan: ${r?.reason || "no new PDFs"}`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scan failed");
        } finally {
            setScanning(false);
        }
    };

    if (!account) return null;

    return (
        <Dialog open={!!open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0E1830] border border-[#2A3655] text-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="email-scan-config-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Inbox className="w-4 h-4 text-[#00A3FF]" /> Scan & Auto-Quote
                        <span className="ml-2 text-xs font-normal text-slate-500">{account.email_address}</span>
                    </DialogTitle>
                </DialogHeader>

                {/* Master toggles */}
                <div className="space-y-3">
                    <ToggleRow
                        label="Scan incoming emails for PDFs"
                        sub="Every 15 min the inbox is checked. PDFs auto-create a new project named from contacts/leads (or email1, email2 for unknown senders)."
                        checked={scanIncoming}
                        onChange={setScanIncoming}
                        testid="scan-incoming-toggle"
                        accent
                    />
                    <ToggleRow
                        label="Auto-quote new PDFs"
                        sub="When ON, an AI estimate is started using the plugins/templates you pick below. The email body is fed in as scope-of-work."
                        checked={autoQuote}
                        onChange={setAutoQuote}
                        testid="auto-quote-toggle"
                        disabled={!scanIncoming}
                    />
                </div>

                {scanIncoming && (
                    <div className="space-y-4 mt-2">
                        {loading ? (
                            <div className="flex items-center text-slate-500 text-sm py-4">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading options…
                            </div>
                        ) : (
                            <>
                                {/* Plugins + Templates only matter when auto-quote
                                    is ON — hide them otherwise to reduce noise. */}
                                {autoQuote && (
                                    <>
                                        <Section title="Plugins to quote" hint="Pick every aspect of the job to quote. Multiple selections will be merged into one combined estimate.">
                                            <PickerGrid
                                                items={plugins}
                                                selected={pluginIds}
                                                onToggle={togglePlugin}
                                                renderLabel={(p) => p.title || p.name || p.id}
                                                testidPrefix="scan-plugin"
                                                emptyText="No plugins configured yet."
                                            />
                                        </Section>

                                        <Section title="Reference pricing templates" hint="Optional. Selected templates supply unit prices when the AI matches their line items.">
                                            <PickerGrid
                                                items={templates}
                                                selected={templateIds}
                                                onToggle={toggleTemplate}
                                                renderLabel={(t) => t.name || t.title || t.id}
                                                testidPrefix="scan-template"
                                                emptyText="No templates yet."
                                            />
                                        </Section>
                                    </>
                                )}

                                {/* Workgroup share */}
                                <Section title="Auto-share to workgroup" hint="Pick a workgroup to receive every email-generated project. Leave empty to keep them personal.">
                                    <select
                                        value={workgroupId}
                                        onChange={(e) => setWorkgroupId(e.target.value)}
                                        data-testid="scan-workgroup-select"
                                        className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm"
                                    >
                                        <option value="">— None (keep personal) —</option>
                                        {workgroups.map((w) => (
                                            <option key={w.id} value={w.id}>{w.name || w.id}</option>
                                        ))}
                                    </select>

                                    {workgroupId && (
                                        <div className="mt-3 space-y-2">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Notify members</div>
                                            <div className="flex flex-wrap gap-2">
                                                {["none", "all", "select"].map((k) => (
                                                    <button
                                                        key={k}
                                                        type="button"
                                                        onClick={() => setNotifyKind(k)}
                                                        data-testid={`scan-notify-kind-${k}`}
                                                        className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border ${
                                                            notifyKind === k
                                                                ? "bg-[#00A3FF] text-white border-[#00A3FF]"
                                                                : "border-[#2A3655] text-slate-300 hover:text-white hover:bg-[#2A3655]"
                                                        }`}
                                                    >
                                                        {k === "none" ? "No one" : k === "all" ? "All members" : "Pick members"}
                                                    </button>
                                                ))}
                                            </div>
                                            {notifyKind === "select" && (
                                                <PickerGrid
                                                    items={members}
                                                    selected={notifyMemberIds}
                                                    onToggle={toggleMember}
                                                    renderLabel={(m) => m.full_name || m.name || m.email || m.user_id}
                                                    keyFn={(m) => m.user_id || m.id}
                                                    testidPrefix="scan-notify-member"
                                                    emptyText="No other members in this workgroup."
                                                />
                                            )}
                                        </div>
                                    )}
                                </Section>
                            </>
                        )}
                    </div>
                )}

                {/* Footer actions */}
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-between pt-4 border-t border-[#2A3655] mt-4">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        {account.last_scanned_at ? (
                            <>Last scan: <span className="text-slate-300">{new Date(account.last_scanned_at).toLocaleString()}</span></>
                        ) : (
                            "Never scanned yet"
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleScanNow}
                            disabled={scanning || !account.scan_incoming}
                            data-testid="scan-now-btn"
                            title={!account.scan_incoming ? "Enable scan first" : "Run a single scan pass now"}
                            className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white hover:bg-[#2A3655] disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                            {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Scan now
                        </button>
                        <button
                            type="button"
                            onClick={() => onOpenChange?.(false)}
                            className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white hover:bg-[#2A3655]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="scan-config-save-btn"
                            className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#00A3FF] hover:bg-[#3395FF] text-white disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Save
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ToggleRow({ label, sub, checked, onChange, testid, accent = false, disabled = false }) {
    return (
        <label
            className={`flex items-start justify-between gap-3 p-3 rounded-sm border ${
                checked && accent ? "border-[#00C853]/60 bg-[#00C853]/5" : "border-[#2A3655] bg-[#0A1128]/60"
            } ${disabled ? "opacity-50" : ""}`}
        >
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">{label}</div>
                {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
            </div>
            <button
                type="button"
                onClick={() => !disabled && onChange(!checked)}
                disabled={disabled}
                data-testid={testid}
                className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors shrink-0 ${
                    checked ? "bg-[#00C853]" : "bg-[#2A3655]"
                }`}
            >
                <span
                    className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        checked ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </button>
        </label>
    );
}

function Section({ title, hint, children }) {
    return (
        <div className="rounded-sm border border-[#2A3655] bg-[#0A1128]/40 p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-200">{title}</div>
            {hint && <div className="text-[11px] text-slate-500 mt-1 mb-2">{hint}</div>}
            <div>{children}</div>
        </div>
    );
}

function PickerGrid({ items, selected, onToggle, renderLabel, keyFn, testidPrefix, emptyText }) {
    if (!items || items.length === 0) {
        return <div className="text-xs italic text-slate-500 py-2">{emptyText}</div>;
    }
    const _key = keyFn || ((it) => it.id);
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {items.map((it) => {
                const k = _key(it);
                const on = selected.includes(k);
                return (
                    <button
                        key={k}
                        type="button"
                        onClick={() => onToggle(k)}
                        data-testid={`${testidPrefix}-${k}`}
                        className={`text-left px-3 py-2 rounded-sm border text-xs transition-colors ${
                            on
                                ? "bg-[#00A3FF]/15 border-[#00A3FF] text-white"
                                : "bg-[#0A1128] border-[#2A3655] text-slate-300 hover:border-[#00A3FF]/60 hover:text-white"
                        }`}
                    >
                        <span className="inline-block w-3 h-3 rounded-sm border mr-2 align-middle"
                            style={{ borderColor: on ? "#00A3FF" : "#475569", background: on ? "#00A3FF" : "transparent" }} />
                        <span className="align-middle">{renderLabel(it)}</span>
                    </button>
                );
            })}
        </div>
    );
}
